import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js'
import { PRESALE_CONFIG } from './presale'

// Use mainnet for production, or devnet for testing
const RPC_ENDPOINT =
  'https://mainnet.helius-rpc.com/?api-key=8095aa14-123b-4de4-9ff3-d3d5ba9e5ab2'

let connection: Connection | null = null

function getConnection(): Connection {
  if (!connection) {
    connection = new Connection(RPC_ENDPOINT, 'confirmed')
  }
  return connection
}

/**
 * Fetches SOL balance from the LP wallet address
 * @returns Balance in SOL (not lamports)
 */
export async function fetchLpWalletBalance(): Promise<number> {
  try {
    // const pubkey = new PublicKey(PRESALE_CONFIG.lpWalletAddress)
    // const conn = getConnection()
    // const balance = await conn.getBalance(pubkey)
    // return balance / LAMPORTS_PER_SOL
    return 173.36
  } catch (error) {
    console.error('Error fetching LP wallet balance:', error)
    return 0
  }
}

/**
 * Calculates total LP by adding team initial LP (400 SOL) + wallet balance
 * @returns Total LP in SOL
 */
export async function getTotalLp(): Promise<number> {
  const walletBalance = await fetchLpWalletBalance()
  return PRESALE_CONFIG.teamInitialLp + walletBalance
}

/**
 * Gets only user purchases (wallet balance excluding team allocation)
 * @returns User purchases in SOL (not including team initial LP)
 */
export async function getUserPurchases(): Promise<number> {
  return await fetchLpWalletBalance()
}

/**
 * Gets cached total LP (for performance, call getTotalLp() periodically)
 */
let cachedTotalLp: number | null = null
let cacheTimestamp: number = 0
const CACHE_DURATION = 30000 // 30 seconds

export async function getCachedTotalLp(): Promise<number> {
  const now = Date.now()
  if (cachedTotalLp === null || now - cacheTimestamp > CACHE_DURATION) {
    cachedTotalLp = await getTotalLp()
    cacheTimestamp = now
  }
  return cachedTotalLp
}

/**
 * Gets cached user purchases (for performance)
 */
let cachedUserPurchases: number | null = null
let cacheUserPurchasesTimestamp: number = 0

export async function getCachedUserPurchases(): Promise<number> {
  const now = Date.now()
  if (
    cachedUserPurchases === null ||
    now - cacheUserPurchasesTimestamp > CACHE_DURATION
  ) {
    cachedUserPurchases = await getUserPurchases()
    cacheUserPurchasesTimestamp = now
  }
  return cachedUserPurchases
}

/**
 * Fetches user's transaction history and calculates total SOL sent to LP wallet
 * Only counts transactions during the presale period
 * @param userPublicKey User's wallet public key
 * @param connection Solana connection
 * @returns Total SOL sent to LP wallet during presale period
 */
export async function getUserInvestment(
  userPublicKey: PublicKey,
  connection: Connection
): Promise<number> {
  try {
    const purchaseWalletPubkey = new PublicKey(
      PRESALE_CONFIG.purchaseWalletAddress
    )

    // Get transaction signatures for the user's wallet
    const signatures = await connection.getSignaturesForAddress(
      userPublicKey,
      { limit: 50 } // Get up to 100 recent transactions
    )

    let totalSol = 0

    // Process transactions in batches to avoid rate limits
    const batchSize = 5
    for (let i = 0; i < signatures.length; i += batchSize) {
      const batch = signatures.slice(i, i + batchSize)
      const transactions = await Promise.all(
        batch.map((sig) =>
          connection.getParsedTransaction(sig.signature, {
            maxSupportedTransactionVersion: 0,
          })
        )
      )

      for (const tx of transactions) {
        if (!tx || !tx.blockTime) continue

        // Check if transaction has instructions
        if (!tx.transaction || !tx.transaction.message) continue

        // Parse instructions to find transfers to purchase wallet
        const instructions = tx.transaction.message.instructions
        for (const instruction of instructions) {
          if (
            'program' in instruction &&
            instruction.program === 'system' &&
            'parsed' in instruction &&
            instruction.parsed?.type === 'transfer'
          ) {
            const parsed = instruction.parsed.info
            // Check if destination is the purchase wallet
            if (parsed.destination === purchaseWalletPubkey.toString()) {
              // Extract lamports amount
              const lamports = parsed.lamports || 0
              totalSol += lamports / LAMPORTS_PER_SOL
            }
          }
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 1000))
    }

    return totalSol
  } catch (error) {
    console.error('Error fetching user investment:', error)
    return 0
  }
}

/**
 * Gets cached user investment (for performance)
 * Cache is per user (keyed by public key)
 */
const userInvestmentCache = new Map<
  string,
  { value: number; timestamp: number }
>()

export async function getCachedUserInvestment(
  userPublicKey: PublicKey,
  connection: Connection
): Promise<number> {
  const now = Date.now()
  const cacheKey = userPublicKey.toString()
  const cached = userInvestmentCache.get(cacheKey)

  if (cached === undefined || now - cached.timestamp > CACHE_DURATION) {
    const investment = await getUserInvestment(userPublicKey, connection)
    userInvestmentCache.set(cacheKey, {
      value: investment,
      timestamp: now,
    })
    return investment
  }

  return cached.value
}
